import { z } from 'zod';

// Adapted from https://github.com/astahmer/openapi-zod-client/blob/main/examples/export-schemas-and-types-directly/schemas-and-types-directly.hbs

const VehicleAvailabilityTour = z
    .object({
        tour_id: z.string().uuid(),
        tour_name: z.string(),
        status: z.enum(['planned', 'in-transit', 'problem']).catch('planned'),
        start: z.string().datetime({ offset: true }),
        end: z.string().datetime({ offset: true }),
    })
    .passthrough();
const VehicleAvailability = z
    .object({
        vehicle_id: z.string().uuid(),
        search_name: z.string(),
        plate: z.string().optional(),
        tours: z.array(VehicleAvailabilityTour),
    })
    .passthrough();
const OrderType = z.enum(['transport', 'service']).catch('transport');
const Comments = z.object({ loading: z.string(), unloading: z.string() }).partial().passthrough();
const ChargingConfiguration = z
    .object({
        mode: z.enum(['target_percentage', 'fixed_duration']).catch('target_percentage'),
        target_percentage: z.number().gt(0).lte(100).optional(),
        charging_duration_seconds: z.number().int().gte(1).lte(86400).optional(),
        charger_power_kw: z.number().gt(0).lte(2000).optional(),
        connector_type: z
            .enum(['iec62196Type2Combo', 'iec62196Type2', 'chademo'])
            .catch('iec62196Type2Combo')
            .optional(),
        voltage_v: z.number().gt(0).lte(2000).optional(),
        current_a: z.number().gt(0).lte(5000).optional(),
        supply_type: z.enum(['dc', 'acSingle', 'acThree']).catch('dc').optional(),
    })
    .passthrough();
const ChargingEstimate = z
    .object({
        arrival_charge_kwh: z.number().gte(0).lte(2000),
        departure_charge_kwh: z.number().gte(0).lte(2000),
        battery_capacity_kwh: z.number().gt(0).lte(2000),
        charging_seconds: z.number().int().gte(0).lte(86400),
        setup_seconds: z.number().int().gte(0).lte(86400),
        calculated_at: z.string().datetime({ offset: true }),
        provider: z.literal('here'),
    })
    .passthrough();
const ChargingDetails = z
    .object({
        configuration: ChargingConfiguration,
        source: z.enum(['manual', 'here']).catch('manual'),
        estimate: ChargingEstimate.optional(),
    })
    .passthrough();
const Location = z
    .object({
        street: z.string(),
        city: z.string(),
        country: z.string(),
        zip_code: z.string(),
    })
    .passthrough();
const StopType = z.enum(['load', 'unload', 'execute']).catch('load');
const Coordinates = z.object({ latitude: z.string(), longitude: z.string() }).passthrough();
const BaseStop = z
    .object({
        charging: ChargingDetails.optional(),
        id: z.string(),
        name: z.string(),
        start_actual: z.string().datetime({ offset: true }).optional(),
        end_actual: z.string().datetime({ offset: true }).optional(),
        start_target: z.string().datetime({ offset: true }),
        end_target: z.string().datetime({ offset: true }),
        location: Location,
        type: StopType,
        coordinates: Coordinates.optional(),
        status: z.enum(['open', 'reached', 'finished']).catch('open'),
    })
    .passthrough();
const BaseMaterial = z
    .object({
        type: z.enum(['cargo-material', 'bulk-material']).catch('cargo-material'),
    })
    .passthrough();
const PackagingType = z
    .enum([
        'unspecified',
        'unpackaged',
        'euro-pallet',
        'industrial-pallet',
        'one-way-pallet',
        'cage-pallet',
        'cardboard-box',
        'rack',
        'roll-container',
        'big-bag',
        'ibc',
        'coil',
        'transport-rack',
    ])
    .catch('unspecified');
const CargoMaterialUnit = z
    .object({
        id: z.string(),
        name: z.string(),
        packaging_type: PackagingType,
        quantity: z
            .object({
                target: z.number().int(),
                loaded: z.number().int().optional(),
                unloaded: z.number().int().optional(),
            })
            .passthrough(),
        weight: z
            .object({ target: z.number(), loaded: z.number(), unloaded: z.number() })
            .partial()
            .passthrough()
            .optional(),
        loading_meter: z
            .object({ target: z.number(), loaded: z.number(), unloaded: z.number() })
            .partial()
            .passthrough()
            .optional(),
        dimensions: z
            .object({
                length: z.number(),
                width: z.number(),
                height: z.number(),
                stacking_factor: z.number(),
            })
            .partial()
            .passthrough()
            .optional(),
        comments: Comments.optional(),
    })
    .passthrough();
const CargoMaterial = BaseMaterial.and(
    z
        .object({
            type: z.literal('cargo-material'),
            units: z.array(CargoMaterialUnit),
        })
        .passthrough()
);
const BulkMaterialType = z.enum(['kilogram', 'ton', 'liter', 'cubic-meter']).catch('kilogram');
const BulkMaterialUnit = z
    .object({
        id: z.string().uuid(),
        name: z.string(),
        unit_type: BulkMaterialType.optional(),
        quantity: z
            .object({
                target: z.number(),
                loaded: z.number().optional(),
                unloaded: z.number().optional(),
            })
            .passthrough(),
        comments: Comments.optional(),
    })
    .passthrough();
const BulkMaterial = BaseMaterial.and(
    z
        .object({
            type: z.literal('bulk-material'),
            units: z.array(BulkMaterialUnit),
        })
        .passthrough()
);
const Materials = z.union([CargoMaterial, BulkMaterial]);
const TransportOrder = z
    .object({
        type: z.literal('transport'),
        id: z.string(),
        tour_id: z.string().optional(),
        truck_id: z.string().optional(),
        tour_name: z.string().optional(),
        tour_created_by: z.string().optional(),
        number: z.string(),
        customer_name: z.string(),
        created_by: z.string().optional(),
        status: z.enum(['open', 'planned', 'in-transit', 'loading', 'unloading', 'finished']).catch('open').optional(),
        comments: Comments.optional(),
        loading_stop: BaseStop,
        unloading_stop: BaseStop,
        materials: z.array(Materials).optional(),
    })
    .passthrough();
const ServiceOrder = z
    .object({
        type: z.literal('service'),
        id: z.string().uuid(),
        tour_id: z.string().uuid().optional(),
        truck_id: z.string().optional(),
        tour_name: z.string().optional(),
        tour_created_by: z.string().optional(),
        order: z.string(),
        category: z
            .enum([
                'cleaning',
                'parking',
                'fueling-or-charging',
                'fueling',
                'charging',
                'assembly',
                'other',
                'tour-start',
                'tour-end',
            ])
            .catch('cleaning')
            .optional(),
        company: z.string().optional(),
        note: z.string().optional(),
        created_by: z.string().optional(),
        status: z.enum(['open', 'planned', 'in-transit', 'executing', 'finished']).catch('open'),
        execution_stop: BaseStop,
    })
    .passthrough();
const Order = z.union([TransportOrder, ServiceOrder]);
const WriteStop = z
    .object({
        charging: ChargingDetails.optional(),
        clear_charging: z.boolean().optional(),
        name: z.string(),
        location: Location,
        start_target: z.string().datetime({ offset: true }),
        end_target: z.string().datetime({ offset: true }),
        type: StopType,
        coordinates: Coordinates.optional(),
    })
    .passthrough();
const WriteCargoMaterialUnit = z
    .object({
        name: z.string(),
        packaging_type: PackagingType,
        quantity: z.object({ target: z.number().int() }).passthrough(),
        weight: z.object({ target: z.number() }).passthrough().optional(),
        loading_meter: z.object({ target: z.number() }).passthrough().optional(),
        dimensions: z
            .object({
                length: z.number(),
                width: z.number(),
                height: z.number(),
                stacking_factor: z.number().int().optional(),
            })
            .passthrough()
            .optional(),
    })
    .passthrough();
const WriteCargoMaterial = BaseMaterial.and(
    z
        .object({
            type: z.literal('cargo-material'),
            units: z.array(WriteCargoMaterialUnit),
        })
        .passthrough()
);
const WriteBulkMaterialUnit = z
    .object({
        name: z.string(),
        unit_type: BulkMaterialType,
        quantity: z.object({ target: z.number() }).passthrough(),
    })
    .passthrough();
const WriteBulkMaterial = BaseMaterial.and(
    z
        .object({
            type: z.literal('bulk-material'),
            units: z.array(WriteBulkMaterialUnit),
        })
        .passthrough()
);
const WriteMaterial = z.union([WriteCargoMaterial, WriteBulkMaterial]);
const WriteTransportOrder = z
    .object({
        type: z.literal('transport'),
        number: z.string(),
        customer_name: z.string(),
        created_by: z.string().optional(),
        comments: Comments.optional(),
        loading_stop: WriteStop,
        unloading_stop: WriteStop,
        materials: z.array(WriteMaterial),
    })
    .passthrough();
const WriteServiceOrder = z
    .object({
        type: z.literal('service'),
        order: z.string(),
        category: z
            .enum([
                'cleaning',
                'parking',
                'fueling-or-charging',
                'fueling',
                'charging',
                'assembly',
                'other',
                'tour-start',
                'tour-end',
            ])
            .catch('cleaning')
            .optional(),
        company: z.string().optional(),
        note: z.string().optional(),
        created_by: z.string().optional(),
        execution_stop: WriteStop,
    })
    .passthrough();
const WriteOrder = z.union([WriteTransportOrder, WriteServiceOrder]);
const DocumentAssociation = z
    .object({
        type: z.enum(['tour', 'tour-stop', 'service-order', 'transport-order', 'problem', 'handling-unit']),
        id: z.string().uuid(),
    })
    .passthrough();
const Document = z
    .object({
        id: z.string().uuid(),
        type: z.enum(['directions', 'receipt', 'proof-of-loading', 'proof-of-delivery', 'other']).catch('directions'),
        name: z.string(),
        file_id: z.string(),
        file_name: z.string(),
        origin: z.enum(['dispatcher', 'driver']).catch('dispatcher').optional(),
        associations: z.array(DocumentAssociation).optional(),
    })
    .passthrough();
const Documents = z.array(Document);
const DocumentList = z.object({ items: Documents }).partial().passthrough();
const WriteDocument = z
    .object({
        type: z.enum(['directions', 'receipt', 'proof-of-loading', 'proof-of-delivery', 'other']).catch('directions'),
        name: z.string(),
        file_id: z.string(),
        file_name: z.string(),
    })
    .passthrough();
const TourCapacity = z
    .object({
        max_weight_kg: z.number().gte(0).lte(1.7976931348623157e308),
        max_loading_meter: z.number().gte(0).lte(1.7976931348623157e308),
        max_volume_liter: z.number().gte(0).lte(1.7976931348623157e308),
        max_volume_cubic_meter: z.number().gte(0).lte(1.7976931348623157e308),
    })
    .partial()
    .passthrough();
const TourProgress = z
    .object({
        total_stop_amount: z.number().int(),
        finished_stop_amount: z.number().int(),
    })
    .passthrough();
const TourProblem = z
    .object({
        id: z.string(),
        type: z
            .enum([
                'other',
                'call-back',
                'declined-goods',
                'problem-with-customer',
                'customer-not-available',
                'accident',
                'heavy-traffic',
                'damage',
                'running-late',
            ])
            .catch('other'),
    })
    .passthrough();
const BaseTour = z
    .object({
        id: z.string(),
        note: z.string().optional(),
        search_name: z.string(),
        driver_id: z.string().optional(),
        truck_id: z.string(),
        trailer_id: z.string().optional(),
        capacity: TourCapacity.optional(),
        progress: TourProgress,
        status: z.enum(['open', 'planned', 'in-transit', 'finished', 'problem']).catch('open'),
        problem: TourProblem.optional(),
    })
    .passthrough();
const TourListEntry = BaseTour.and(
    z
        .object({ stops: z.array(BaseStop) })
        .partial()
        .passthrough()
);
const ChargingDraft = z.object({ client_id: z.string().min(1).max(100), execution_stop: WriteStop }).passthrough();
const UpdateStop = z
    .object({
        charging: ChargingDetails.optional(),
        clear_charging: z.boolean().optional(),
        name: z.string(),
        location: Location,
        start_target: z.string().datetime({ offset: true }),
        end_target: z.string().datetime({ offset: true }),
        comment: z.string().optional(),
        coordinates: Coordinates.optional(),
    })
    .passthrough();
const CreateTourStopReference = z
    .object({
        charging_draft_id: z.string().optional(),
        order_id: z.string().optional(),
        type: StopType,
        index: z.number().int(),
        stop_update: UpdateStop.optional(),
    })
    .passthrough();
const WriteTour = z
    .object({
        charging_drafts: z.array(ChargingDraft).max(100).optional(),
        note: z.string().max(255).optional(),
        search_name: z.string(),
        driver_id: z.string().optional(),
        truck_id: z.string(),
        trailer_id: z.string().optional(),
        created_by: z.string().optional(),
        capacity: TourCapacity.optional(),
        stops: z.array(CreateTourStopReference),
    })
    .passthrough();
const TransportDetailedStop = BaseStop.and(
    z
        .object({
            type: z.enum(['load', 'unload']).catch('load'),
            materials: z.array(Materials),
        })
        .passthrough()
);
const ServiceDetailedStop = BaseStop.and(z.object({ type: z.literal('execute') }).passthrough());
const DetailedStop = z.union([TransportDetailedStop, ServiceDetailedStop]);
const Tour = BaseTour.and(
    z
        .object({ stops: z.array(DetailedStop) })
        .partial()
        .passthrough()
);
const DuplicateTourStopReference = CreateTourStopReference.and(z.object({ order_id: z.string() }).passthrough());
const DuplicateTour = WriteTour.and(
    z
        .object({
            date: z.string(),
            time_zone: z.string(),
            stops: z.array(DuplicateTourStopReference).optional(),
        })
        .passthrough()
);
const DuplicateOrderReference = z.object({ temp_id: z.string() }).passthrough();
const DuplicateTransportOrder = WriteTransportOrder.and(DuplicateOrderReference);
const DuplicateServiceOrder = WriteServiceOrder.and(DuplicateOrderReference);
const DuplicateOrder = z.union([DuplicateTransportOrder, DuplicateServiceOrder]);
const WriteTourDuplicate = z.object({ tour: DuplicateTour, orders: z.array(DuplicateOrder) }).passthrough();
const ArchiveJobStatus = z.enum(['IDLE', 'RUNNING', 'COMPLETED', 'COMPLETED_PARTIAL', 'FAILED']);
const TourDocumentArchiveJobStatus = z
    .object({
        tourId: z.string().uuid(),
        status: ArchiveJobStatus,
        documentHash: z.string().min(64).max(64),
        fileName: z.string(),
        partial: z.boolean(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        startedAt: z.string().datetime({ offset: true }).optional(),
        finishedAt: z.string().datetime({ offset: true }).optional(),
        expiresAt: z.string().datetime({ offset: true }).optional(),
        errorMessage: z.string().optional(),
    })
    .passthrough();
const TourDocumentArchiveDownload = z
    .object({
        tourId: z.string().uuid(),
        documentHash: z.string().min(64).max(64),
        fileName: z.string(),
        partial: z.boolean(),
        downloadUrl: z.string().url(),
        expiresAt: z.string().datetime({ offset: true }),
    })
    .passthrough();
const Driver = z.object({ id: z.string(), name: z.string() }).passthrough();
const Vehicle = z
    .object({
        id: z.string(),
        search_name: z.string(),
        plate: z.string().optional(),
        group_ids: z.array(z.string().uuid()).optional(),
        has_license: z.boolean().optional(),
        has_pocket_driver_data_source: z.boolean().nullish(),
    })
    .passthrough();

export const schemas = {
    VehicleAvailabilityTour,
    VehicleAvailability,
    OrderType,
    Comments,
    ChargingConfiguration,
    ChargingEstimate,
    ChargingDetails,
    Location,
    StopType,
    Coordinates,
    BaseStop,
    BaseMaterial,
    PackagingType,
    CargoMaterialUnit,
    CargoMaterial,
    BulkMaterialType,
    BulkMaterialUnit,
    BulkMaterial,
    Materials,
    TransportOrder,
    ServiceOrder,
    Order,
    WriteStop,
    WriteCargoMaterialUnit,
    WriteCargoMaterial,
    WriteBulkMaterialUnit,
    WriteBulkMaterial,
    WriteMaterial,
    WriteTransportOrder,
    WriteServiceOrder,
    WriteOrder,
    DocumentAssociation,
    Document,
    Documents,
    DocumentList,
    WriteDocument,
    TourCapacity,
    TourProgress,
    TourProblem,
    BaseTour,
    TourListEntry,
    ChargingDraft,
    UpdateStop,
    CreateTourStopReference,
    WriteTour,
    TransportDetailedStop,
    ServiceDetailedStop,
    DetailedStop,
    Tour,
    DuplicateTourStopReference,
    DuplicateTour,
    DuplicateOrderReference,
    DuplicateTransportOrder,
    DuplicateServiceOrder,
    DuplicateOrder,
    WriteTourDuplicate,
    ArchiveJobStatus,
    TourDocumentArchiveJobStatus,
    TourDocumentArchiveDownload,
    Driver,
    Vehicle,
};
